import { useEffect, useMemo, useState } from 'react';
import { CheckCircle, RefreshCcw, UploadCloud, X } from 'lucide-react';
import { CheckoutPayload, createPaymentLink, GenerationPreview, getPreview, retryGeneration } from '../lib/api';
import { getConfig } from '../lib/config';
import { comparisonImages } from '../lib/samples';
import { Reviews } from '../components/Reviews';
import { getClientId } from '../lib/usage';
import { rememberActiveRoot } from '../lib/active-root';
import { estimatePortraitProgress, PortraitProgressStage } from '../lib/progress';

type PackageType = 'instant' | 'refined';

interface PackageCardContent {
  badge?: string;
  title: string;
  subtitle?: string;
  comparePrice?: string;
  price: string;
  description: string;
  bullets: string[];
  noteLabel?: string;
  noteHelp?: string;
  notePlaceholder?: string;
  buttonLabel: string;
  loadingLabel: string;
}

const defaultPackages: Record<PackageType, PackageCardContent> = {
  instant: {
    title: 'Instant Download Portrait',
    comparePrice: '$20.00',
    price: '$15.00',
    description: 'Download your version instantly, perfect for sharing, saving, or keeping as a memory.',
    bullets: ['No watermark on your final portrait', 'Download instantly, no waiting', '4K resolution portrait'],
    buttonLabel: 'Download Now',
    loadingLabel: 'Opening Checkout...'
  },
  refined: {
    badge: 'Customer Favorite',
    title: 'Refined Portrait',
    subtitle: '(Print Ready)',
    comparePrice: '$30.00',
    price: '$20.00',
    description: 'A professionally refined version of your selected portrait, optimized for printing.',
    bullets: ['Your selected demo in high resolution', 'Hand-crafted by our team', 'Delivered via email within 48 hours'],
    noteLabel: 'Note',
    notePlaceholder: 'Add your note here',
    buttonLabel: 'Order Now',
    loadingLabel: 'Opening Checkout...'
  }
};

function getPreviewId() {
  const params = new URLSearchParams(window.location.search);
  return params.get('preview') || params.get('id') || '';
}

function getPreviewToken() {
  return new URLSearchParams(window.location.search).get('token') || '';
}

function previewPath(id: string, token?: string) {
  const params = new URLSearchParams({ preview: id });
  if (token) params.set('token', token);
  return `/?${params.toString()}`;
}

function uploadPath(rootId: string, token?: string) {
  const params = new URLSearchParams({ uploadRoot: rootId });
  if (token) params.set('token', token);
  return `/?${params.toString()}`;
}

function accessTokenKey(id: string) {
  return `portrait_access_token:${id}`;
}

function readAccessToken(id: string) {
  if (!id) return '';
  return getPreviewToken() || window.sessionStorage.getItem(accessTokenKey(id)) || '';
}

function rememberAccessToken(id: string, token?: string) {
  if (!id || !token) return;
  window.sessionStorage.setItem(accessTokenKey(id), token);
}

function mergePackageContent(type: PackageType, config?: Partial<PackageCardContent>): PackageCardContent {
  const defaults = defaultPackages[type];
  return {
    ...defaults,
    ...config,
    badge: config?.badge?.trim() || defaults.badge,
    title: config?.title?.trim() || defaults.title,
    subtitle: config?.subtitle?.trim() || defaults.subtitle,
    comparePrice: config?.comparePrice?.trim() || defaults.comparePrice,
    price: config?.price?.trim() || defaults.price,
    description: config?.description?.trim() || defaults.description,
    noteLabel: config?.noteLabel?.trim() || defaults.noteLabel,
    noteHelp: config?.noteHelp?.trim() || defaults.noteHelp,
    notePlaceholder: config?.notePlaceholder?.trim() || defaults.notePlaceholder,
    buttonLabel: config?.buttonLabel?.trim() || defaults.buttonLabel,
    loadingLabel: config?.loadingLabel?.trim() || defaults.loadingLabel,
    bullets: config?.bullets?.filter((bullet) => bullet.trim()).length
      ? config.bullets.filter((bullet) => bullet.trim())
      : defaults.bullets
  };
}

function RetryProcessingView({ progress }: { progress: number }) {
  return (
    <div className="retry-processing">
      <div className="processing-box retry-processing-box">
        <div className="processing-copy">
          <span>Generating a new portrait version...</span>
          <small>We will keep checking until the new version is ready.</small>
        </div>
        <div className="progress-track" aria-label="Retry progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress} role="progressbar">
          <span style={{ width: `${progress}%` }} />
        </div>
        <strong className="processing-percent">{progress}%</strong>
      </div>
    </div>
  );
}

export function PreviewPage() {
  const id = useMemo(getPreviewId, []);
  const [preview, setPreview] = useState<GenerationPreview>();
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [activeId, setActiveId] = useState(id);
  const [activeAccessToken, setActiveAccessToken] = useState(() => readAccessToken(id));
  const [didNormalizeVersion, setDidNormalizeVersion] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showRetry, setShowRetry] = useState(false);
  const [isRetrying, setRetrying] = useState(false);
  const [retryStartedAt, setRetryStartedAt] = useState<number>();
  const [preparingStartedAt, setPreparingStartedAt] = useState<number>(() => Date.now());
  const [progressTick, setProgressTick] = useState(0);
  const [loadingProduct, setLoadingProduct] = useState<'instant' | 'refined'>();
  const config = getConfig();
  const isPreparingPreview = !!activeId && (!preview || (preview.status !== 'completed' && preview.status !== 'failed'));

  useEffect(() => {
    if (!activeId) return;
    setPreparingStartedAt(Date.now());
    setProgressTick(0);
  }, [activeId]);

  useEffect(() => {
    if (!activeId) return;
    let active = true;
    let timer: number | undefined;

    async function poll() {
      try {
        const next = await getPreview(activeId, activeAccessToken || readAccessToken(activeId));
        if (!active) return;
        setImageError(false);
        rememberAccessToken(next.id, next.accessToken);
        rememberAccessToken(next.rootId || next.id, next.accessToken || activeAccessToken);
        rememberActiveRoot(next.rootId || next.id, next.accessToken || activeAccessToken);
        if (next.accessToken && next.id === activeId) setActiveAccessToken(next.accessToken);
        setPreview(next);
        if (next.status !== 'completed' && next.status !== 'failed') {
          timer = window.setTimeout(poll, 3500);
        }
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Could not load preview.');
      }
    }

    poll();
    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [activeAccessToken, activeId]);

  useEffect(() => {
    if ((!isRetrying && !isPreparingPreview) || (!retryStartedAt && !preparingStartedAt)) return;
    const timer = window.setInterval(() => setProgressTick((tick) => tick + 1), 1000);
    return () => window.clearInterval(timer);
  }, [isPreparingPreview, isRetrying, preparingStartedAt, retryStartedAt]);

  useEffect(() => {
    if (!isRetrying || !retryStartedAt) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [isRetrying, retryStartedAt]);

  useEffect(() => {
    if (!isRetrying || preview?.status !== 'completed') return;
    setRetrying(false);
    setRetryStartedAt(undefined);
  }, [isRetrying, preview?.status]);

  useEffect(() => {
    if (didNormalizeVersion || !preview?.versions?.length) return;
    setDidNormalizeVersion(true);
  }, [didNormalizeVersion, preview?.versions]);

  useEffect(() => {
    if (!preview?.previewUrl) {
      setImageError(false);
      return;
    }

    let active = true;
    const image = new Image();

    image.onload = () => {
      if (!active) return;
      setImageError(false);
    };
    image.onerror = () => {
      if (!active) return;
      setImageError(true);
    };
    image.src = preview.previewUrl;

    return () => {
      active = false;
      image.onload = null;
      image.onerror = null;
    };
  }, [preview?.previewUrl]);

  async function buy(productType: 'instant' | 'refined') {
    if (!activeId) return;
    setLoadingProduct(productType);
    setError('');
    try {
      const variantId = productType === 'instant' ? config.instantVariantId : config.refinedVariantId;
      const checkout = await createPaymentLink(activeId, { productType, accessToken: activeAccessToken, variantId, personalizedNote: note });
      await startShopifyCheckout(checkout);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create payment link.');
      setLoadingProduct(undefined);
    }
  }

  async function retry() {
    if (!activeId) return;
    if ((preview?.remainingUses ?? 1) <= 0) {
      setToast('You have run out of trial uses for today, please come back tomorrow.');
      setShowRetry(false);
      return;
    }

    setRetrying(true);
    setRetryStartedAt(Date.now());
    setProgressTick(0);
    setShowRetry(false);
    setError('');
    try {
      const result = await retryGeneration(activeId, activeAccessToken, getClientId(), config.trademark);
      setActiveId(result.id);
      setActiveAccessToken(result.accessToken || activeAccessToken);
      rememberAccessToken(result.id, result.accessToken || activeAccessToken);
      setPreview(undefined);
      window.history.replaceState(null, '', previewPath(result.id, result.accessToken || activeAccessToken));
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Could not retry this portrait.');
      setRetrying(false);
      setRetryStartedAt(undefined);
    }
  }

  if (!id) {
    return (
      <div className="preview-flow">
        <h1>Preview Not Found</h1>
        <p className="center-copy">Go back to the upload page to create your portrait demo.</p>
      </div>
    );
  }

  const isReady = preview?.status === 'completed';
  const packageHeading = config.packages?.heading || 'Choose Your';
  const packageHeadingEmphasis = config.packages?.headingEmphasis || 'Version';
  const instantPackage = mergePackageContent('instant', config.packages?.instant);
  const refinedPackage = mergePackageContent('refined', config.packages?.refined);
  const comparisonGallery = config.comparisonImages?.filter(Boolean).length ? config.comparisonImages.filter(Boolean) : comparisonImages;
  const versions = preview?.versions?.length
    ? preview.versions
    : preview
      ? [{ id: preview.id, versionNumber: preview.versionNumber || 1, status: preview.status, previewUrl: preview.previewUrl }]
      : [];
  const remainingUses = Math.max(0, preview?.remainingUses ?? 0);
  const previewProgressStage: PortraitProgressStage =
    preview?.status === 'failed'
      ? 'failed'
      : preview?.status === 'completed'
        ? imageError
          ? 'finalizing'
          : 'completed'
        : preview?.status === 'queued'
          ? 'queued'
          : preview?.status === 'processing'
            ? preview.previewUrl
              ? 'finalizing'
              : 'processing'
            : 'queued';
  const previewProgress = estimatePortraitProgress(preparingStartedAt, previewProgressStage, progressTick);
  const retryProgress = estimatePortraitProgress(retryStartedAt, previewProgressStage, progressTick);
  const previewStatusText =
    preview?.status === 'failed'
      ? 'Generation failed'
      : preview?.status === 'queued'
        ? 'Queued for portrait generation...'
        : preview?.status === 'processing'
          ? 'Creating your portrait...'
          : 'Creating preview...';

  return (
    <div className="preview-flow">
      {toast && (
        <div className="usage-toast">
          <span>{toast}</span>
          <button type="button" onClick={() => setToast('')} aria-label="Close notice">
            <X size={18} />
          </button>
        </div>
      )}

      <h1 className="preview-title">
        {isReady ? (
          <>
            Your Portrait is Ready - <em>Choose</em> The One That Speaks To You
          </>
        ) : (
          <>
            Your Portrait is <em>Preparing</em>
          </>
        )}
      </h1>

      {isRetrying && <RetryProcessingView progress={retryProgress} />}

      <div className="portrait-stage">
        {isReady && preview?.previewUrl && !imageError ? (
          <img
            alt="Generated pet portrait preview"
            src={preview.previewUrl}
            onError={() => setImageError(true)}
            onLoad={() => setImageError(false)}
          />
        ) : imageError && isReady && preview?.previewUrl ? (
          <div className="portrait-placeholder is-recoverable">
            <span>Preview image is temporarily unavailable.</span>
            <small>Your portrait is ready and checkout is still available below.</small>
            <a href={preview.previewUrl} target="_blank" rel="noreferrer">
              Open preview image
            </a>
          </div>
        ) : (
          <div className="portrait-placeholder">
            <span>
              {preview?.status === 'failed'
                ? 'Generation failed'
                : isPreparingPreview
                  ? previewStatusText
                  : imageError
                  ? 'Preview image is still loading. Please retry in a moment.'
                  : 'Creating preview...'}
            </span>
            {preview?.status !== 'failed' && (
              <>
                <div className="progress-track compact" aria-label="Preview progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={previewProgress} role="progressbar">
                  <span style={{ width: `${previewProgress}%` }} />
                </div>
                <small className="processing-percent">{previewProgress}%</small>
              </>
            )}
          </div>
        )}
        <button className="retry-button" type="button" onClick={() => setShowRetry(true)}>
          <RefreshCcw size={15} /> Retry or Edit
        </button>
      </div>

      <div className="version-tabs" aria-label="Portrait versions">
        {versions.map((version) => (
          <button
            className={version.id === activeId ? 'is-active' : ''}
            key={version.id}
            type="button"
            onClick={() => {
              setDidNormalizeVersion(true);
              setActiveId(version.id);
              const nextToken = version.accessToken || activeAccessToken || readAccessToken(version.id);
              setActiveAccessToken(nextToken || '');
              rememberAccessToken(version.id, nextToken);
              window.history.replaceState(null, '', previewPath(version.id, nextToken));
            }}
          >
            Version {version.versionNumber}
          </button>
        ))}
      </div>

      {showRetry && (
        <div className="retry-overlay" role="dialog" aria-modal="true" aria-label="Retry options">
          <div className="retry-modal">
            <button className="retry-close" type="button" onClick={() => setShowRetry(false)} aria-label="Close retry options">
              <X size={20} />
            </button>
            <h2>Retry Options</h2>
            <p>{remainingUses} remaining - Each actions uses 1</p>
            <div className="retry-actions">
              <button type="button" className="retry-action is-green" disabled={isRetrying || remainingUses <= 0} onClick={retry}>
                <RefreshCcw size={28} />
                <span>{isRetrying ? 'Retrying...' : 'Retry'}</span>
              </button>
              <button
                type="button"
                className="retry-action is-dark"
                disabled={remainingUses <= 0}
                onClick={() => {
                  const nextToken = activeAccessToken || readAccessToken(activeId);
                  rememberAccessToken(activeId, nextToken);
                  window.location.href = activeId ? uploadPath(activeId, nextToken) : '/';
                }}
              >
                <UploadCloud size={28} />
                <span>Upload New</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <h2 className="choose-heading">
        {packageHeading} <em>{packageHeadingEmphasis}</em>
      </h2>

      {error && <p className="form-error">{error}</p>}

      <div className="package-grid">
        <PackageCard content={instantPackage} isReady={isReady} loadingProduct={loadingProduct} type="instant" onBuy={buy} />
        <PackageCard
          content={refinedPackage}
          isFeatured
          isReady={isReady}
          loadingProduct={loadingProduct}
          note={note}
          type="refined"
          onBuy={buy}
          onNoteChange={setNote}
        />
      </div>

      <div className="comparison-grid">
        {comparisonGallery.map((image) => (
          <img alt="" key={image} src={image} />
        ))}
      </div>

      <Reviews config={config.reviews} />
    </div>
  );
}

async function startShopifyCheckout(checkout: CheckoutPayload) {
  if (!checkout.variantId) {
    window.location.href = checkout.url || '/cart';
    return;
  }

  await shopifyCartRequest('/cart/clear.js', {});
  await shopifyCartRequest('/cart/update.js', {
    attributes: {
      generation_id: '',
      personalized_note: '',
      pawbond_delivery_message: '',
      pawbond_delivery_status: '',
      pawbond_download_url: '',
      pawbond_final_image_url: '',
      pawbond_preview_url: '',
      pawbond_product_type: ''
    }
  });
  await shopifyCartRequest('/cart/add.js', {
    items: [
      {
        id: Number(checkout.variantId),
        quantity: checkout.quantity || 1
      }
    ]
  });
  await shopifyCartRequest('/cart/update.js', {
    attributes: checkout.attributes || {}
  });

  window.location.href = checkout.checkoutUrl || '/checkout';
}

async function shopifyCartRequest(path: string, body: unknown) {
  const response = await fetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    throw new Error('Could not prepare Shopify checkout.');
  }
}

function PackageCard({
  content,
  isFeatured,
  isReady,
  loadingProduct,
  note,
  type,
  onBuy,
  onNoteChange
}: {
  content: PackageCardContent;
  isFeatured?: boolean;
  isReady: boolean;
  loadingProduct?: PackageType;
  note?: string;
  type: PackageType;
  onBuy: (type: PackageType) => void;
  onNoteChange?: (value: string) => void;
}) {
  return (
    <article className={`package-card ${isFeatured ? 'is-featured' : ''}`}>
      {content.badge && <span className="favorite-pill">{content.badge}</span>}
      <h3>{content.title}</h3>
      {content.subtitle && <p className="subtitle">{content.subtitle}</p>}
      <p className="price">
        {content.comparePrice && <s>{content.comparePrice}</s>} {content.price}
      </p>
      <p>{content.description}</p>
      <ul>
        {content.bullets.map((bullet) => (
          <li key={bullet}>
            <CheckCircle size={16} /> {bullet}
          </li>
        ))}
      </ul>
      {type === 'refined' && (
        <label>
          {content.noteLabel || 'Note'}
          {content.noteHelp && <small>{content.noteHelp}</small>}
          <textarea
            placeholder={content.notePlaceholder || 'Add your note here'}
            value={note || ''}
            onChange={(event) => onNoteChange?.(event.currentTarget.value)}
          />
        </label>
      )}
      <button disabled={!isReady || !!loadingProduct} type="button" onClick={() => onBuy(type)}>
        {loadingProduct === type ? content.loadingLabel : content.buttonLabel}
      </button>
    </article>
  );
}
